import { FormSectionHandler } from '../entity.handler';
import { UserPasswordPolicy } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { CheckListField, ComboField, NumberField } from '../utils';

interface EditorValues {
    source?: string;

    passwordLength: number;
    passwordComplexity: any[];

    passwordExpirationDays: string;

    otpTypes: string[];
    otpMandatoryFor: string;
    otpGracePeriodDays: string;
    otpTokenValidityDays: string;

}

interface LimitedCompanyResponse {
    id?: string;
    passwordPolicy?: UserPasswordPolicy;
}

const PASSWORD_COMPLEXITY_KEYS = ['mixedCase', 'alphaNumerical', 'specialCharacters', 'noCommonPasswords'];
const OTP_TYPES = [
    { key: 'email', value: 'Emailed One Time Password' },
    { key: 'totp', value: 'Time-based One Time Password' },
]

export class PasswordPolicySectionHandler<T extends LimitedCompanyResponse> implements FormSectionHandler<T, EditorValues> {

    type = 'form';
    icon = 'key';
    bulk = false;
    hidden = false;

    title = this.translate('TITLE');
    description = ''

    actions = [];
    originalValues: EditorValues;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public entityType: string
    ) {
        this.description = entityType === 'companygroup' ? this.translate('DESC_COMPANYGROUP') : this.translate('DESC_COMPANY');
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.PASSWORD_POLICY.${key}`, values);
    }

    applyUpdate(entity: T, payload: any): Promise<any> {
        switch (this.entityType) {
            case 'client': return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload), { _cacheKey: `getClient/${entity.id}` });
            case 'vendor': return this.admin.applyUpdate(api => api.accounts.updateVendor(entity.id, payload), { _cacheKey: `getVendor/${entity.id}` });
            case 'distributor': return this.admin.applyUpdate(api => api.accounts.updateDistributor(entity.id, payload), { _cacheKey: `getDistributor/${entity.id}` });
            case 'companygroup': return this.admin.applyUpdate(api => api.accounts.updateCompanyGroup(entity.id, payload), { _cacheKey: `getCompanyGroup/${entity.id}` });
            default:
                throw new Error(`Update for '${this.entityType}' not supported`);
        }
    }

    async mapEntityToEditor(entity: LimitedCompanyResponse): Promise<EditorValues> {

        // we currently only support setting token validity days for all of the types the same, map them out here
        const methods = entity.passwordPolicy?.otpSettings?.methods || [];
        const tokenValidityDays = Object.keys(methods).reduce((result, key) => {
            return Math.max(result, methods[key].tokenValidityDays);
        }, 0) || 0;


        this.originalValues = {
            source: entity.passwordPolicy?.source?.id === entity.id ? 'custom' : 'owner',
            passwordLength: entity.passwordPolicy?.passwordLength || 8,
            passwordComplexity: PASSWORD_COMPLEXITY_KEYS.filter(x => entity.passwordPolicy?.passwordComplexity[x] || false),
            passwordExpirationDays: entity.passwordPolicy?.passwordExpirationDays?.toString() || '0',
            otpTypes: Object.keys(methods),
            otpMandatoryFor: entity.passwordPolicy?.otpSettings?.mandatoryFor || 'optional',
            otpGracePeriodDays: entity.passwordPolicy?.otpSettings?.gracePeriodDays?.toString() || '7',
            otpTokenValidityDays: tokenValidityDays.toString(),
        };
        return { ...this.originalValues };
    }

    async mapEditorToEntity(_entity: LimitedCompanyResponse, values: EditorValues): Promise<LimitedCompanyResponse> {
        if (values?.source === 'owner') {
            return { passwordPolicy: null };
        }

        const complexity = values?.passwordComplexity || this.originalValues.passwordComplexity || [];

        const methods = OTP_TYPES.map(x => x.key);
        const enabledMethods = (values?.otpTypes || this.originalValues.otpTypes);

        const result: LimitedCompanyResponse = {
            passwordPolicy: {
                passwordLength: values?.passwordLength || this.originalValues.passwordLength,
                passwordComplexity: {
                    alphaNumerical: complexity.includes('alphaNumerical'),
                    mixedCase: complexity.includes('mixedCase'),
                    specialCharacters: complexity.includes('specialCharacters'),
                    noCommonPasswords: complexity.includes('noCommonPasswords')
                },
                passwordExpirationDays: values?.passwordExpirationDays !== undefined ? parseFloat(values.passwordExpirationDays) : parseFloat(this.originalValues.passwordExpirationDays),
                otpSettings: {
                    methods: methods.reduce((result, key) => {
                        if (enabledMethods.includes(key)) {
                            result[key] = {
                                tokenValidityDays: values?.otpTokenValidityDays ? parseFloat(values?.otpTokenValidityDays) : parseFloat(this.originalValues.otpTokenValidityDays)
                            };
                        } else {
                            result[key] = null;
                        }
                        return result;
                    }, {}),
                    mandatoryFor: (values?.otpMandatoryFor || this.originalValues.otpMandatoryFor) as any,
                    gracePeriodDays: values?.otpGracePeriodDays ? parseFloat(values?.otpGracePeriodDays) : parseFloat(this.originalValues.otpGracePeriodDays),
                },
            },
        };
        return result;
    }

    async getFields(entity: LimitedCompanyResponse): Promise<FormBuilderField[]> {

        const complexity = PASSWORD_COMPLEXITY_KEYS.map(x => ({ key: x, value: this.translate('FIELDS.REQUIRE_' + x.toUpperCase()) }));

        let ownerType = entity?.passwordPolicy?.source?.type || 'owner';
        if (entity?.passwordPolicy?.source?.id === entity.id) {
            ownerType = 'owner';
        }
        const ownerName = this.translate(`FIELDS.SOURCE_VALUES.${ownerType.toUpperCase()}`);

        const getFields = (readonly: boolean) => {
            return [
                NumberField('passwordLength', this.translate('FIELDS.PASSWORD_LENGTH'), true, 8, 100, ''),
                { ...CheckListField('passwordComplexity', 'Password Complexity', false, complexity), getText: (field, values) => (values[field.id] || []).map(x => complexity.find(bv => bv.key === x)?.value), },
                ComboField('passwordExpirationDays', this.translate('FIELDS.PASSWORD_EXPIRATION'), true, [
                    { key: '0', value: 'Never' },
                    ...['7', '14', '30', '60', '90', '365'].map(x => ({ key: x, value: this.admin.i18n.instant('SHARED.DISPLAY_TIME.DAYS', { d: x }) })),
                ]),
                ComboField('otpMandatoryFor', this.translate('FIELDS.OTP_MANDATORY_FOR'), true, [
                    { key: 'optional', value: this.translate('FIELDS.OTP_MANDATORY_FOR_OPTIONAL') },
                    { key: 'everyone', value: this.translate('FIELDS.OTP_MANDATORY_FOR_EVERYONE') },
                    { key: 'administrators', value: this.translate('FIELDS.OTP_MANDATORY_FOR_ADMIN') },

                ]),
                { ...CheckListField('otpTypes', this.translate('FIELDS.OTP_TYPES'), false, OTP_TYPES), getText: (field, values) => (values[field.id] || []).map(x => OTP_TYPES.find(bv => bv.key === x)?.value) },
                {
                    ...ComboField('otpGracePeriodDays', this.translate('FIELDS.OTP_GRACE_PERIOD'), true, [
                        { key: '0', value: 'None' },
                        ...['1', '7', '14', '30'].map(x => ({ key: x, value: this.admin.i18n.instant('SHARED.DISPLAY_TIME.DAYS', { d: x }) })),
                    ]),
                    description: this.translate('FIELDS.OTP_GRACE_PERIOD_DESC')
                },
                {
                    ...ComboField('otpTokenValidityDays', this.translate('FIELDS.OTP_VALIDITY_PERIOD'), true, [
                        { key: '0', value: 'None' },
                        ...['1', '7', '14', '30'].map(x => ({ key: x, value: this.admin.i18n.instant('SHARED.DISPLAY_TIME.DAYS', { d: x }) })),
                    ]),
                    description: this.translate('FIELDS.OTP_VALIDITY_PERIOD_DESC')
                },
            ].map(x => ({ ...x, readonly: readonly }));
        }

        if (this.entityType === 'companygroup' || this.entityType === 'distributor') { // these do not support inheriting settings
            return getFields(false);
        } else {
            return [ComboField('source', this.translate('FIELDS.POLICY_SOURCE'), true, [
                {
                    key: 'owner', value: ownerName, fields: getFields(true),
                },
                {
                    key: 'custom', value: this.translate('FIELDS.SOURCE_VALUES.CUSTOM'), fields: getFields(false),
                },
            ])];
        }

    }

}
